import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { css, cx } from '@emotion/css'
import { Row, Box } from '../../legacy-design-system/form'
import { CsHelpCircledIcon as HelpIcon } from '../../legacy-design-system/icons'
import { GetFormFieldPropsFn } from '../../legacy-design-system/form/form-validator'
import { theme as deprecatedTheme } from '../../styled'
import {
  TextInput,
  FormTextContainer
} from '../../legacy-design-system/form-text'
import { WithTooltip } from '../../design-system'
import { DatePickerDropdown } from '../../legacy-design-system/date-picker/date-picker-dropdown'
import { DatePicker } from '../../legacy-design-system/date-picker/date-picker'
import { DateOption } from '../../legacy-design-system/date-picker/shared'
import { SourceName } from '.'
import { getSourceNameEditRoute } from '../route'
import { IPCIDR } from './ipcidr'
import { Text, Link } from '@contrast-security-inc/design-system-typography'
import {
  theme,
  IconAdd,
  IconClose
} from '@contrast-security-inc/design-system-foundations'
import { useSourceNameFormContext } from './source-name-form-context'

interface Props {
  getFormFieldProps: GetFormFieldPropsFn
  setCustomValidity?: (id: string, validity: boolean) => void
  organizationID: string
  existingSourceNames?: SourceName[]
}

function HelpText({ children }) {
  return (
    <Text
      as="p"
      font="metadata"
      color="white"
      className={css`
        max-width: 260px;
        overflow-wrap: normal;
        white-space: normal;
      `}
    >
      {children}
    </Text>
  )
}

const datePickerContainerCss = css`
  position: absolute;
  z-index: 1;
  padding: 4px 0;
  border-radius: 1px;
  border-width: 1px;
  border-style: solid;
  background-color: ${theme.color.white};
  border-color: ${theme.color.neutral40};
`
const startDateOptions: DateOption[] = [
  DateOption.NOW,
  DateOption.THIRTY_MINUTES,
  DateOption.ONE_DAY,
  DateOption.ONE_WEEK,
  DateOption.CUSTOM
]
const endDateOptions: DateOption[] = [
  DateOption.THIRTY_MINUTES,
  DateOption.ONE_DAY,
  DateOption.ONE_WEEK,
  DateOption.NEVER,
  DateOption.CUSTOM
]

const IP_RANGE_LIMIT = 100

export const SourceNameForm: React.FC<Props> = ({
  getFormFieldProps,
  organizationID,
  setCustomValidity,
  existingSourceNames
}) => {
  const intl = useIntl()
  const { state, dispatch } = useSourceNameFormContext()
  const getSourceNameErrorMessages =
    (sourceNameRequiredMessage: string, uniqueSourceNameMessage: string) =>
    ({ validity, validationMessage }) => {
      if (validity && !validity.valid && validity.valueMissing) {
        return sourceNameRequiredMessage
      }
      if (
        existingSourceNames &&
        existingSourceNames
          .map((item) => {
            return item.name
          })
          .includes(state.name)
      ) {
        return uniqueSourceNameMessage
      }
      return validationMessage
    }

  const getCidrErrorMessages =
    (
      cidrBlock: string,
      cidrRequiredMessage: string,
      invalidCidrMessage: string
    ) =>
    ({ validationMessage }) => {
      if (cidrBlock.length === 0) {
        return cidrRequiredMessage
      }
      const cidr = new IPCIDR(cidrBlock)

      if (!cidr.isValid()) {
        return invalidCidrMessage
      }
      return validationMessage
    }

  const getDateErrorMessage = (start?: Date, expiration?: Date) => {
    if (start && expiration && start >= expiration) {
      return intl.formatMessage({
        id: 'validation_requiredStartTimeBeforeEndTime'
      })
    }
    if (!!!start && expiration && new Date() >= expiration) {
      return intl.formatMessage({
        id: 'validation_requiredStartTimeBeforeEndTime'
      })
    }

    return undefined
  }

  const addMinutes = (date, minutes) =>
    new Date(date.getTime() + minutes * 60000)

  const startDates = [
    undefined,
    addMinutes(new Date(), 30),
    addMinutes(new Date(), 60 * 24),
    addMinutes(new Date(), 60 * 168)
  ]

  const expirationDates = [
    undefined,
    addMinutes(new Date(), 30),
    addMinutes(new Date(), 60 * 24),
    addMinutes(new Date(), 60 * 168),
    undefined
  ]

  return (
    <div
      className={css`
        margin-left: 12px;
      `}
    >
      <FormattedMessage
        id="detail_sourceNames_description"
        children={(msg) => (
          <div
            className={css`
              margin-top: 24px;
              margin-bottom: 20px;
            `}
          >
            <Text font="body" color="neutral80">
              {msg}
            </Text>
          </div>
        )}
      />
      <form noValidate>
        <Row>
          <Box basis={3}>
            <TextInput
              data-e2e="source-name-form-name-input"
              label={intl.formatMessage({
                id: 'title_name'
              })}
              value={state.name}
              placeholder={intl.formatMessage({
                id: 'placeholder_sourceNames_name'
              })}
              onChange={(value) => {
                dispatch({ type: 'EDIT_NAME', payload: value })
              }}
              required={true}
              {...getFormFieldProps({
                id: 'sourceName_name',
                getErrorMessage: getSourceNameErrorMessages(
                  intl.formatMessage({
                    id: 'validation_requiredName'
                  }),
                  intl.formatMessage({
                    id: 'validation_uniqueSourceName'
                  })
                ),
                validator: ({ validity }) => {
                  if (validity.valid && existingSourceNames) {
                    return !existingSourceNames
                      .map((item) => {
                        return item.name
                      })
                      .includes(state.name)
                  }

                  return validity.valid
                }
              })}
            />
          </Box>
        </Row>
        <Row>
          <Box basis={9}>
            <div
              className={css`
                margin-top: 12px;
              `}
            >
              <FormattedMessage
                id="title_ipAddressOrSubnetMask"
                children={(msg) => (
                  <span
                    className={css`
                      padding-top: 20px;
                      color: ${theme.color.neutral60};
                      font-weight: ${deprecatedTheme.fontWeights.primary.bold};
                    `}
                  >{`${msg} *`}</span>
                )}
              />
              <div
                className={css`
                  display: inline-block;
                  vertical-align: text-top;
                `}
              >
                <WithTooltip
                  renderTooltipContent={() => (
                    <div>
                      <FormattedMessage
                        id="title_sourceNames_ipAddress_tooltip"
                        children={(msg) => (
                          <Text as="p" font="metadataStrong" color="white">
                            {msg}
                          </Text>
                        )}
                      />
                      <FormattedMessage
                        id="detail_sourceNames_ipAddress_tooltip"
                        children={(msg) => <HelpText>{msg}</HelpText>}
                      />
                    </div>
                  )}
                  placement="right"
                >
                  {({ onMouseEnter, onMouseLeave }) => (
                    <div
                      className={css`
                        margin-left: 4px;
                        margin-top: -4px;
                      `}
                    >
                      <HelpIcon
                        color={theme.color.neutral50}
                        height={16}
                        width={16}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                      />
                    </div>
                  )}
                </WithTooltip>
              </div>
            </div>
            <SourceNameOverlapWarning
              existingSourceNames={existingSourceNames}
              cidrBlocks={state.cidrBlocks}
              organizationID={organizationID}
            />
            {state.cidrBlocks.map((cidrBlock, index) => (
              <Row key={index}>
                <Box basis={4}>
                  <div
                    className={css`
                      display: flex;
                      align-items: center;
                    `}
                  >
                    <TextInput
                      data-e2e="source-name-form-ip-address-input"
                      key={index}
                      label=""
                      value={cidrBlock}
                      onChange={(value) => {
                        dispatch({
                          type: 'EDIT_CIDR_BLOCK',
                          payload: { value, index }
                        })
                      }}
                      placeholder="10.0.0.0"
                      {...getFormFieldProps({
                        id: `sourceName_ip_${index}`,
                        getErrorMessage: getCidrErrorMessages(
                          cidrBlock,
                          intl.formatMessage({
                            id: 'validation_requiredIPAddressSubnetMask'
                          }),
                          intl.formatMessage({
                            id: 'validation_invalidIPAddressSubnetMask'
                          })
                        ),
                        validator: ({ validity }) => {
                          if (validity.valid && existingSourceNames) {
                            const cidr = new IPCIDR(cidrBlock)

                            return cidrBlock.length > 0 && cidr.isValid()
                          }

                          return validity.valid
                        }
                      })}
                    />
                  </div>
                </Box>
                <span
                  className={css`
                    padding-top: 16px;
                  `}
                >
                  {state.cidrBlocks.length > 1 && (
                    <div
                      data-testid={`remove-cidr-block-${index}`}
                      style={{ cursor: 'pointer', marginLeft: '8px' }}
                      onClick={() => {
                        dispatch({ type: 'REMOVE_CIDR_BLOCK', payload: index })
                      }}
                    >
                      <IconClose size="sm" color="neutral100" />
                    </div>
                  )}
                </span>
              </Row>
            ))}
            {state.cidrBlocks.length < IP_RANGE_LIMIT && (
              <div
                className={css`
                  display: flex;
                  align-items: center;
                `}
              >
                <IconAdd size="sm" />{' '}
                <FormattedMessage
                  id="title_sourceNames_addIpAddress"
                  children={(msg) => (
                    <Link
                      font="body"
                      onClick={() =>
                        dispatch({ type: 'ADD_CIDR_BLOCK', payload: '' })
                      }
                    >
                      {msg}
                    </Link>
                  )}
                />
              </div>
            )}
          </Box>
        </Row>
        <div
          className={css`
            margin-top: 15px;
            margin-bottom: 10px;
          `}
        >
          <FormTextContainer
            errorMessage={getDateErrorMessage(
              state.startDate,
              state.expirationDate
            )}
          >
            {() => {
              return (
                <Row>
                  <Box basis={2}>
                    <DatePickerDropdown
                      labelID="title_start_dateRange"
                      dateOptions={startDateOptions}
                      selectedDate={state.startDate}
                      selectedOption={state.startOption}
                      onDateOptionSelect={(value) => {
                        const payload: {
                          startOption: DateOption
                          startDate?: Date
                        } = { startOption: value }
                        if (value !== DateOption.CUSTOM) {
                          payload.startDate = startDates[value]
                        }
                        dispatch({ type: 'EDIT_DATE_OPTION', payload })
                        if (setCustomValidity && value !== DateOption.CUSTOM) {
                          setCustomValidity(
                            'startDate',
                            getDateErrorMessage(
                              startDates[value],
                              state.expirationDate
                            ) === undefined
                          )
                        }
                      }}
                      onCustomDateSelect={(value) => {
                        dispatch({
                          type: 'EDIT_DATE',
                          payload: { startDate: value }
                        })
                        if (setCustomValidity) {
                          setCustomValidity(
                            'startDate',
                            getDateErrorMessage(value, state.expirationDate) ===
                              undefined
                          )
                        }
                      }}
                      renderDatePicker={(props) => (
                        <div
                          className={cx(
                            'legacy-ds-date-picker-container',
                            datePickerContainerCss
                          )}
                        >
                          <DatePicker
                            {...props}
                            initialDate={state.startDate}
                            initialMonth={state.startDate}
                          />
                        </div>
                      )}
                    />
                  </Box>
                  <Box basis={2}>
                    <DatePickerDropdown
                      labelID="title_end_dateRange"
                      dateOptions={endDateOptions}
                      selectedDate={state.expirationDate}
                      selectedOption={state.expirationOption}
                      onDateOptionSelect={(value) => {
                        const payload: {
                          expirationOption: DateOption
                          expirationDate?: Date
                        } = { expirationOption: value }
                        if (value !== DateOption.CUSTOM) {
                          payload.expirationDate = expirationDates[value]
                        }
                        dispatch({ type: 'EDIT_DATE_OPTION', payload })
                        if (setCustomValidity && value !== DateOption.CUSTOM) {
                          setCustomValidity(
                            'startDate',
                            getDateErrorMessage(
                              state.startDate,
                              expirationDates[value]
                            ) === undefined
                          )
                        }
                      }}
                      onCustomDateSelect={(value) => {
                        dispatch({
                          type: 'EDIT_DATE',
                          payload: { expirationDate: value }
                        })
                        if (setCustomValidity) {
                          setCustomValidity(
                            'startDate',
                            getDateErrorMessage(state.startDate, value) ===
                              undefined
                          )
                        }
                      }}
                      renderDatePicker={(props) => (
                        <div
                          className={cx(
                            'legacy-ds-date-picker-container',
                            datePickerContainerCss
                          )}
                        >
                          <DatePicker
                            {...props}
                            initialMonth={state.expirationDate}
                            initialDate={state.expirationDate}
                          />
                        </div>
                      )}
                    />
                  </Box>
                </Row>
              )
            }}
          </FormTextContainer>
        </div>
      </form>
    </div>
  )
}

export function SourceNameOverlapWarning({
  existingSourceNames,
  cidrBlocks,
  organizationID
}: {
  readonly existingSourceNames?: SourceName[]
  readonly cidrBlocks: string[]
  readonly organizationID: string
}) {
  const sourceNameConflicts = getSourceNameConflicts({
    existingSourceNames,
    cidrBlocks
  })
  return (
    <React.Fragment>
      {sourceNameConflicts && sourceNameConflicts.length > 0 && (
        <div
          className={css`
            font-family: ${deprecatedTheme.fontFamilies.secondary};
            padding: 10px 0 10px 0;
            color: ${theme.color.neutral60};
          `}
        >
          <FormattedMessage
            id="validation_uniqueSourceNameIPPlural_link"
            values={{
              count: sourceNameConflicts.length,
              link: (
                <React.Fragment>
                  {sourceNameConflicts.map((sourceName, index) => {
                    return (
                      <a
                        key={sourceName.id}
                        href={getSourceNameEditRoute(
                          organizationID,
                          sourceName.id
                        )}
                        target="_blank"
                      >
                        {sourceName.name}
                        {index !== sourceNameConflicts.length - 1 && (
                          <span
                            className={css`
                              padding-right: 3px;
                            `}
                          >
                            ,
                          </span>
                        )}
                      </a>
                    )
                  })}
                </React.Fragment>
              )
            }}
          />
        </div>
      )}
    </React.Fragment>
  )
}

export function getSourceNameConflicts({
  existingSourceNames,
  cidrBlocks
}: {
  readonly existingSourceNames?: SourceName[]
  readonly cidrBlocks: string[]
}) {
  const overlappingSourceNames: SourceName[] = []
  cidrBlocks.forEach((newCidrBlock) => {
    const newCidr = new IPCIDR(newCidrBlock)

    if (newCidrBlock.length > 0 && newCidr.isValid() && existingSourceNames) {
      existingSourceNames.forEach((sourceName) => {
        sourceName.cidrBlocks.forEach((existingCidr) => {
          if (newCidr.contains(existingCidr)) {
            overlappingSourceNames.push(sourceName)
          }
        })
      })
    }
  })

  return overlappingSourceNames
}
